import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

function formatUterineMass(mass) {
  const parts = [];
  if (mass.echotexture) parts.push(mass.echotexture);
  if (mass.locationType) parts.push(mass.locationType);
  if (mass.location) parts.push(`at ${mass.location}`);
  const size = [mass.size1, mass.size2, mass.size3].filter(Boolean).join(' x ');
  if (size) parts.push(`(${size} cm)`);
  if (mass.description) parts.push(`— ${mass.description}`);
  return parts.join(' ');
}

function formatOvaryMass(mass) {
  const parts = [];
  if (mass.echotexture) parts.push(mass.echotexture);
  const size = [mass.size1, mass.size2, mass.size3].filter(Boolean).join(' x ');
  if (size) parts.push(`(${size} cm)`);
  if (mass.description) parts.push(`— ${mass.description}`);
  return parts.join(' ');
}

export function PelvicPDF({ data, facilityInfo }) {
  const parts = [];
  if (data.reason && String(data.reason).trim()) parts.push(String(data.reason).trim());
  const histStr = historyToString(data.history);
  if (histStr) parts.push(histStr);
  if (data.premenopausal) parts.push('Premenopausal');
  if (data.perimenopausal) parts.push('Perimenopausal');
  if (data.postmenopausal) parts.push('Postmenopausal');
  if (data.hrt) parts.push('HRT');
  if (data.iud) parts.push('IUD');
  // Supracervical hysterectomy removes the uterine body but leaves the cervix in place
  const supracervical = !!data.supracervicalHysterectomy;
  const uterusRemoved = !!data.hysterectomy || supracervical;
  const hystLabel = supracervical ? 'Supracervical Hysterectomy' : 'Hysterectomy';
  if (uterusRemoved) parts.push(hystLabel);
  const reason = parts.length === 0 ? 'Unspecified' : parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('. ');

  const uterus = data.uterus || {};
  const endo = data.endometrium || {};
  const cervix = data.cervix || {};
  const ro = data.rightOvary || {};
  const lo = data.leftOvary || {};
  const culDeSac = data.culDeSac || {};
  const bladder = data.bladder || {};

  const uterineVol = (uterus.length && uterus.width && uterus.depth)
    ? calculateVolume(uterus.length, uterus.width, uterus.depth) : null;
  const roVol = (ro.length && ro.width && ro.depth)
    ? calculateVolume(ro.length, ro.width, ro.depth) : null;
  const loVol = (lo.length && lo.width && lo.depth)
    ? calculateVolume(lo.length, lo.width, lo.depth) : null;

  const measurementsText = [
    uterusRemoved ? `Uterus: ${hystLabel}` : [
      (uterus.length && uterus.width && uterus.depth)
        ? `Uterus: ${uterus.length} x ${uterus.width} x ${uterus.depth} cm${uterineVol ? ` (Vol: ${uterineVol} cc)` : ''}` : null,
      endo.thickness ? `Endometrium: ${endo.thickness} mm` : null,
    ].filter(Boolean).join('\n'),
    ro.status && ro.status.some(s => s === 'Not visualized') ? 'Right ovary: Not visualized' :
      (ro.length && ro.width && ro.depth)
        ? `Right ovary: ${ro.length} x ${ro.width} x ${ro.depth} cm${roVol ? ` (Vol: ${roVol} cc)` : ''}` : null,
    lo.status && lo.status.some(s => s === 'Not visualized') ? 'Left ovary: Not visualized' :
      (lo.length && lo.width && lo.depth)
        ? `Left ovary: ${lo.length} x ${lo.width} x ${lo.depth} cm${loVol ? ` (Vol: ${loVol} cc)` : ''}` : null,
  ].filter(Boolean).join('\n');

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="PELVIC ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfMeasurementBox title="MEASUREMENTS">
              <Text style={commonStyles.infoBoxText}>{measurementsText || 'None recorded'}</Text>
            </PdfMeasurementBox>
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            <PdfAccentSectionHeader title="UTERUS" />
            {uterusRemoved ? (
              <PdfLabelValue label="Status:" value={hystLabel} forceBlack />
            ) : (
              <>
                <PdfLabelValue label="Echotexture:" value={uterus.echotexture || ''} isAbnormal={uterus.echotexture && uterus.echotexture !== 'Normal'} />
                <PdfLabelValue label="Position:" value={uterus.position || ''} forceBlack />
                {uterus.other && <PdfLabelValue label="Other:" value={uterus.other} isAbnormal />}
                {uterus.masses && uterus.masses.length > 0 && (
                  <PdfLabelValue label="Masses:" value={uterus.masses.filter(Boolean).map(formatUterineMass).join('\n')} isAbnormal />
                )}
              </>
            )}

            <PdfAccentSectionHeader title="ENDOMETRIUM" />
            {uterusRemoved ? (
              <PdfLabelValue label="Status:" value={`N/A (${hystLabel})`} forceBlack />
            ) : (
              <>
                <PdfLabelValue label="Status:" value={endo.status || ''} isAbnormal={endo.status && endo.status !== 'Normal'} />
                {endo.other && <PdfLabelValue label="Other:" value={endo.other} isAbnormal />}
              </>
            )}

            <PdfAccentSectionHeader title="CERVIX" />
            {data.hysterectomy && !supracervical ? (
              <PdfLabelValue label="Status:" value="Absent (Hysterectomy)" forceBlack />
            ) : (
              <>
                <PdfLabelValue label="Status:" value={cervix.status || ''} isAbnormal={cervix.status && cervix.status !== 'Normal'} />
                {cervix.other && <PdfLabelValue label="Other:" value={cervix.other} isAbnormal />}
              </>
            )}

            <PdfAccentSectionHeader title="RIGHT OVARY" />
            {ro.status && ro.status.length > 0 && (
              <PdfLabelValue label="Status:" value={ro.status.filter(Boolean).join(', ')} isAbnormal={ro.status.some(s => s && s !== 'Normal')} />
            )}
            {ro.other && <PdfLabelValue label="Other:" value={ro.other} isAbnormal />}
            {ro.masses && ro.masses.length > 0 && (
              <PdfLabelValue label="Masses:" value={ro.masses.filter(Boolean).map(formatOvaryMass).join('\n')} isAbnormal />
            )}

            <PdfAccentSectionHeader title="LEFT OVARY" />
            {lo.status && lo.status.length > 0 && (
              <PdfLabelValue label="Status:" value={lo.status.filter(Boolean).join(', ')} isAbnormal={lo.status.some(s => s && s !== 'Normal')} />
            )}
            {lo.other && <PdfLabelValue label="Other:" value={lo.other} isAbnormal />}
            {lo.masses && lo.masses.length > 0 && (
              <PdfLabelValue label="Masses:" value={lo.masses.filter(Boolean).map(formatOvaryMass).join('\n')} isAbnormal />
            )}

            <PdfAccentSectionHeader title="CUL-DE-SAC / BLADDER" />
            <PdfLabelValue label="Free Fluid:" value={culDeSac.fluid ? 'Present' : 'None'} isAbnormal={!!culDeSac.fluid} />
            {culDeSac.other && <PdfLabelValue label="Cul-de-sac other:" value={culDeSac.other} isAbnormal />}
            <PdfLabelValue label="Bladder:" value={bladder.status || ''} isAbnormal={bladder.status && bladder.status !== 'Normal'} />
            {bladder.other && <PdfLabelValue label="Bladder other:" value={bladder.other} isAbnormal />}
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
